import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* A refusal is a real HTTP status carrying { code, message } — there is no
   status field in the body to test. The text is what the screens render, so
   the code is only the fallback when a route answers without a sentence. */
const apiError = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

/* FETCH WITH SEARCH
   { data, pagination } — the inner `data` is the payload's own key. The
   search matches the sub-category name OR its parent category's name.
   Rows: id, name, slug, imageUrl, sortOrder, isActive, categoryId,
   categoryName. */
export const fetchSubCategories = createAsyncThunk(
  "subCategory/fetch",
  async ({ page = 1, limit = 10, search = "" } = {}, thunkAPI) => {
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) qs.set("search", search);
      const res = await api.get(`/admin/catalog/subcategories?${qs.toString()}`);
      return res.data;
    } catch (err) {
      /* Node answered every failure with HTTP 200, so this thunk never had to
         reject. The service answers 401/403 for real, and without a
         rejectWithValue the screen gets a SerializedError object it renders as
         nothing at all. */
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch subcategories"));
    }
  }
);

/* FETCH ALL SUBCATEGORIES (NO PAGINATION)
   { data: [...] } — inactive rows included, capped at 5000 server-side. */
export const fetchAllSubCategories = createAsyncThunk(
  "subCategory/fetchAll",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(`/admin/catalog/subcategories/all`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch all subcategories"));
    }
  }
);

/* CREATE
   JSON, not multipart: { name, categoryId, image | imageUrl, sortOrder,
   status | isActive }. There is no image upload endpoint, so the image
   travels as the URL string the form already holds. 201 { data: <row> };
   422 unknown_category, 409 duplicate. */
export const createSubCategory = createAsyncThunk(
  "subCategory/create",
  async (body, thunkAPI) => {
    try {
      const res = await api.post(`/admin/catalog/subcategories`, body);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to create subcategory"));
    }
  }
);

/* UPDATE
   { data: <row> }. An absent key leaves the stored value alone, so a partial
   body no longer wipes the name or the parent. `formData` keeps its name for
   the callers, but it is a plain JSON object now. */
export const updateSubCategory = createAsyncThunk(
  "subCategory/update",
  async ({ id, formData }, thunkAPI) => {
    try {
      const res = await api.put(`/admin/catalog/subcategories/${id}`, formData);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to update subcategory"));
    }
  }
);

/* DELETE
   Superadmin only; refuses with 409 in_use while products still point at it,
   and 400 system_managed for the Millables pair. */
export const deleteSubCategory = createAsyncThunk(
  "subCategory/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/admin/catalog/subcategories/${id}`);
      return id;
    } catch (err) {
      /* The refusal is the whole point of this route now: 409 in_use carries
         "3 product(s) still use this sub-category." Letting the thunk reject
         with a raw Error threw that sentence away and handed the caller's
         toast an object instead of text. */
      return thunkAPI.rejectWithValue(apiError(err, "Delete failed"));
    }
  }
);

/* TOGGLE STATUS
   No body — it flips the stored flag and answers
   { data: { id, name, isActive, categoryId, categoryName } }. */
export const toggleSubCategoryStatus = createAsyncThunk(
  "subCategory/toggle",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/admin/catalog/subcategories/${id}/status`);
      return res.data.data;
    } catch (err) {
      /* 403 for a branch account, 404 for an unknown id — both real statuses
         now, and both silent unless the refusal is carried out of the thunk. */
      return thunkAPI.rejectWithValue(apiError(err, "Failed to update status"));
    }
  }
);

/* FETCH BY CATEGORY
   { success, data: [{ id, name }] } — active rows only, and an unknown
   category answers an empty list rather than a 404. */
export const fetchSubCategoriesByCategory = createAsyncThunk(
  "subCategory/fetchByCategory",
  async (categoryId, thunkAPI) => {
    try {
      const res = await api.get(`/admin/catalog/categories/${categoryId}/subcategories`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch subcategories"));
    }
  }
);

const subCategorySlice = createSlice({
  name: "subCategory",
  initialState: {
    subCategories: [],
    allSubCategories: [], // New state for all subcategories
    loading: false,
    error: null,
    pagination: {}
  },
  reducers: {
    clearSubCategories: (state) => {
      state.subCategories = [];
    },
    clearAllSubCategories: (state) => {
      state.allSubCategories = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.subCategories = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchSubCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllSubCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllSubCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.allSubCategories = action.payload.data || action.payload;
      })
      .addCase(fetchAllSubCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSubCategory.fulfilled, (state, action) => {
        state.subCategories.unshift(action.payload);
        // Also add to allSubCategories if it exists
        if (state.allSubCategories.length > 0) {
          state.allSubCategories.unshift(action.payload);
        }
      })
      /* A sub-category is addressed by `id`; the service returns no `_id`.
         The PUT answers { id, name, slug, imageUrl, sortOrder, isActive } and
         nothing else — no categoryId, no categoryName — so the row is merged
         rather than replaced. Swapping it in wholesale blanked the Category
         column of the row that had just been saved. */
      .addCase(updateSubCategory.fulfilled, (state, action) => {
        const index = state.subCategories.findIndex(
          (sub) => sub.id === action.payload.id
        );
        if (index !== -1) {
          Object.assign(state.subCategories[index], action.payload);
        }
        // Update in allSubCategories too
        const allIndex = state.allSubCategories.findIndex(
          (sub) => sub.id === action.payload.id
        );
        if (allIndex !== -1) {
          Object.assign(state.allSubCategories[allIndex], action.payload);
        }
      })
      .addCase(deleteSubCategory.fulfilled, (state, action) => {
        state.subCategories = state.subCategories.filter(
          (sub) => sub.id !== action.payload
        );
        // Remove from allSubCategories too
        state.allSubCategories = state.allSubCategories.filter(
          (sub) => sub.id !== action.payload
        );
      })
      /* A refused delete used to be impossible — Node answered 200 whatever it
         did. 409 in_use and 400 system_managed are real now, so the sentence is
         kept where the screen can read it. */
      .addCase(deleteSubCategory.rejected, (state, action) => {
        state.error = action.payload;
      })
      // The toggle answers { id, name, isActive, categoryId, categoryName } —
      // no image or slug — so the flag is merged rather than swapped in
      .addCase(toggleSubCategoryStatus.fulfilled, (state, action) => {
        const index = state.subCategories.findIndex(
          (sub) => sub.id === action.payload.id
        );
        if (index !== -1) {
          state.subCategories[index].isActive = action.payload.isActive;
        }
        // Update in allSubCategories too
        const allIndex = state.allSubCategories.findIndex(
          (sub) => sub.id === action.payload.id
        );
        if (allIndex !== -1) {
          state.allSubCategories[allIndex].isActive = action.payload.isActive;
        }
      })
      /* The toggle is dispatched without unwrap, so a 403 from a branch account
         is invisible unless it lands here. */
      .addCase(toggleSubCategoryStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchSubCategoriesByCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubCategoriesByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.subCategories = action.payload;
      })
      .addCase(fetchSubCategoriesByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearSubCategories, clearAllSubCategories } = subCategorySlice.actions;
export default subCategorySlice.reducer;